import type { Program } from '../../types/index.js'
import { isBroadcastableMedia } from '../../lib/utils.js'

/**
 * « Prêt pour l'antenne » — miroir de `backend/src/lib/program-readiness.ts`.
 *
 * Les deux copies existent pour que le badge s'affiche sans aller-retour
 * réseau pendant la saisie, le serveur restant seul juge à l'enregistrement.
 * Modifier l'une sans l'autre fait diverger ce que l'opérateur voit de ce que
 * le serveur compte : grep `programReadiness` des deux côtés avant d'éditer.
 *
 * Quatre exigences bloquantes — titre TrafficPlay, titre EPG, genre, média
 * principal. Le résumé est recommandé : une fiche sans résumé part
 * correctement, une fiche sans genre arrive dans une case vide du magazine de
 * programmes. Un direct n'attend aucun fichier, l'exigence média ne lui est pas
 * applicable.
 */

export type ReadinessCheckId = 'title' | 'epgTitle' | 'genre' | 'media' | 'summary'

export interface ReadinessCheck {
  id: ReadinessCheckId
  label: string
  /** Précision sous le libellé : valeur retenue, ou ce qui manque. */
  detail?: string | null
  required: boolean
  satisfied: boolean
}

export interface ProgramReadiness {
  ready: boolean
  checks: ReadinessCheck[]
  satisfiedRequired: number
  totalRequired: number
}

/** Un champ vide peut être `null` (effacé) ou `''` (import ancien). */
const filled = (value?: string | null): boolean => typeof value === 'string' && value.trim().length > 0

/**
 * Libellé du genre effectivement publié.
 *
 * Même chaîne de replis que l'export EPG : la liste fermée d'abord, puis le
 * texte libre hérité tant que toutes les fiches ne sont pas rattachées.
 */
export function effectiveGenreLabel(program: Pick<Program, 'programGenre' | 'genre'>): string | null {
  return program.programGenre?.label ?? program.genre ?? null
}

/**
 * La fiche dispose d'un fichier diffusable.
 *
 * Les parties comptent : sur un programme découpé, ce sont elles qui portent
 * les médias, et n'interroger que la racine signalerait « sans média » une
 * fiche entièrement servie.
 */
export function hasBroadcastableMedia(program: Pick<Program, 'mediaAssets' | 'parts'>): boolean {
  const own = program.mediaAssets?.some(asset => isBroadcastableMedia(asset.status)) ?? false
  if (own) return true
  return (program.parts ?? []).some(part =>
    part.mediaAssets?.some(asset => isBroadcastableMedia(asset.status)) ?? false)
}

export function programReadiness(
  program: Pick<Program, 'title' | 'titleShort' | 'summary' | 'programGenre' | 'genre' | 'isLive' | 'mediaAssets' | 'parts'>,
): ProgramReadiness {
  const genreLabel = effectiveGenreLabel(program)
  const hasMedia = hasBroadcastableMedia(program)

  const checks: ReadinessCheck[] = [
    {
      id: 'title', label: 'Titre TrafficPlay', required: true,
      satisfied: filled(program.title),
      detail: filled(program.title) ? 'Renseigné' : 'Manquant',
    },
    {
      id: 'epgTitle', label: 'Titre EPG', required: true,
      satisfied: filled(program.titleShort),
      detail: filled(program.titleShort) ? 'Renseigné' : 'Manquant',
    },
    {
      id: 'genre', label: 'Genre', required: true,
      satisfied: filled(genreLabel),
      detail: filled(genreLabel) ? genreLabel!.trim() : 'Aucune rubrique — case vide au guide',
    },
    {
      id: 'media', label: 'Média principal',
      // Le direct sort de la liste des exigences plutôt que d'y figurer comme
      // satisfait : il n'a rien à fournir, et le compter donnerait un score
      // atteint pour une raison qui n'existe pas.
      required: !program.isLive,
      satisfied: Boolean(program.isLive || hasMedia),
      detail: program.isLive ? 'Direct — aucun fichier attendu'
        : hasMedia ? 'Disponible' : 'Aucun fichier associé',
    },
    {
      id: 'summary', label: 'Résumé EPG', required: false,
      satisfied: filled(program.summary),
      detail: 'Optionnel',
    },
  ]

  const required = checks.filter(check => check.required)

  return {
    ready:             required.every(check => check.satisfied),
    checks,
    satisfiedRequired: required.filter(check => check.satisfied).length,
    totalRequired:     required.length,
  }
}
